import { useEffect, useState, useMemo } from 'react';
import { BookOpen, Loader2, Download, Search, ChevronRight, Lightbulb, HelpCircle } from 'lucide-react';
import API from '../lib/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';

const downloadWord = async () => {
  try {
    const r = await API.get('/erp/manual/docx', { responseType: 'blob' });
    const u = URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = u; a.download = 'Manual-SAGA-Infrastructura.docx'; a.click(); URL.revokeObjectURL(u);
  } catch { /* noop */ }
};

function Shot({ sectionId }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let obj;
    API.get(`/erp/manual/screenshot/${sectionId}`, { responseType: 'blob' })
      .then(r => { obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => {});
    return () => obj && URL.revokeObjectURL(obj);
  }, [sectionId]);
  if (!url) return null;
  return <img src={url} alt="Captură de ecran" className="w-full rounded-lg border border-white/10 my-3" data-testid={`manual-shot-${sectionId}`} />;
}

export default function ManualDrawer({ open: openProp, onOpenChange }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = onOpenChange || setOpenState;
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => { if (open && data === null) API.get('/erp/manual').then(r => setData(r.data)).catch(() => setData(false)); }, [open, data]);

  const sections = useMemo(() => {
    if (!data?.sections) return [];
    if (!q.trim()) return data.sections;
    const t = q.toLowerCase();
    return data.sections.map(s => {
      const hit = txt => (txt || '').toLowerCase().includes(t);
      if (hit(s.title) || hit(s.intro)) return s;
      const actions = (s.actions || []).filter(a => hit(a.title) || (a.steps || []).some(hit) || (a.tips || []).some(hit));
      return actions.length ? { ...s, actions } : null;
    }).filter(Boolean);
  }, [data, q]);

  return <>
    <button onClick={() => setOpen(true)} data-testid="manual-open-btn"
      className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:scale-105 transition-transform"
      aria-label="Deschide manualul de utilizare">
      <HelpCircle className="w-5 h-5" /><span className="text-sm font-medium hidden sm:inline">Manual</span>
    </button>

    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="manual-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-blue-400" />Manual de utilizare</SheetTitle>
          <SheetDescription>Ghid pas-cu-pas pentru fiecare modul și acțiune.</SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-2 my-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Caută (jurnal, factură, alocare)…" className="pl-9" data-testid="manual-search" />
          </div>
          <Button onClick={downloadWord} variant="outline" data-testid="manual-download"><Download className="w-4 h-4 mr-2" />Word</Button>
        </div>

        {data === null ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          : data === false ? <p className="text-sm text-muted-foreground" data-testid="manual-error">Nu am putut încărca manualul.</p>
          : <div className="space-y-5 pb-10">
              {!sections.length && <p className="text-sm text-muted-foreground" data-testid="manual-no-results">Niciun rezultat pentru „{q}".</p>}
              {sections.map(s => <section key={s.id} className="border border-white/5 rounded-xl p-4" data-testid={`manual-section-${s.id}`}>
                <h3 className="text-base font-semibold flex items-center gap-2 text-blue-300"><BookOpen className="w-4 h-4" />{s.title}</h3>
                {s.intro && <p className="text-sm text-muted-foreground mt-1.5">{s.intro}</p>}
                {s.image && <Shot sectionId={s.id} />}
                <div className="mt-3 space-y-4">
                  {(s.actions || []).map((a, i) => <div key={i} data-testid={`manual-action-${s.id}-${i}`}>
                    <p className="text-sm font-medium flex items-center gap-2"><ChevronRight className="w-4 h-4 text-blue-400" />{a.title}</p>
                    <ol className="mt-1.5 ml-6 space-y-1 list-decimal text-sm text-foreground/90">
                      {(a.steps || []).map((st, j) => <li key={j}>{st}</li>)}
                    </ol>
                    {(a.tips || []).map((tip, k) => <p key={k} className="mt-1.5 ml-6 text-xs text-emerald-300/90 flex items-start gap-1.5"><Lightbulb className="w-3.5 h-3.5 mt-0.5 shrink-0" />{tip}</p>)}
                  </div>)}
                </div>
              </section>)}
            </div>}
      </SheetContent>
    </Sheet>
  </>;
}
